import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { BillingProvider, useBilling } from "@/context/BillingContext";
import { AnalystProvider } from "@/context/AnalystContext";
import Layout from "@/components/Layout";
import AuthCallback from "@/components/AuthCallback";
import AIAssistant from "@/components/AIAssistant";
import Home from "@/pages/Home";
import Properties from "@/pages/Properties";
import PropertyDetail from "@/pages/PropertyDetail";
import Vehicles from "@/pages/Vehicles";
import VehicleDetail from "@/pages/VehicleDetail";
import Market from "@/pages/Market";
import Roadmap from "@/pages/Roadmap";
import Dashboard from "@/pages/Dashboard";
import Portfolio from "@/pages/Portfolio";
import Pricing from "@/pages/Pricing";
import Login from "@/pages/Login";
import CoverageMap from "@/pages/CoverageMap";
import Legal from "@/pages/Legal";
import Admin from "@/pages/Admin";
import { Footer } from "@/components/Footer";
import { ConsentBanner } from "@/components/ConsentBanner";
import "@/index.css";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center font-mono-data text-sm text-muted-foreground animate-pulse">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

// Full paywall: requires login AND an active Vaelith Membership (or admin). Non-members are
// routed straight to Pricing so they can subscribe. Admins bypass via ent.premium.
function Member({ children }) {
  const { user, loading } = useAuth();
  const { ent } = useBilling();
  if (loading) return <div className="min-h-screen flex items-center justify-center font-mono-data text-sm text-muted-foreground animate-pulse">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  // Billing not resolved for this user yet — wait rather than falsely treating them as a non-member.
  if (ent === null) return <div className="min-h-screen flex items-center justify-center font-mono-data text-sm text-muted-foreground animate-pulse">Loading…</div>;
  if (!ent.premium) return <Navigate to="/pricing" replace />;
  return children;
}

function AppRoutes() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/login" element={<Login />} />
        <Route path="/homes" element={<Member><Properties /></Member>} />
        <Route path="/homes/:id" element={<Member><PropertyDetail /></Member>} />
        <Route path="/vehicles" element={<Member><Vehicles /></Member>} />
        <Route path="/vehicles/:id" element={<Member><VehicleDetail /></Member>} />
        <Route path="/market" element={<Member><Market /></Member>} />
        <Route path="/roadmap" element={<Member><Roadmap /></Member>} />
        <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
        <Route path="/portfolio" element={<Member><Portfolio /></Member>} />
        <Route path="/directory" element={<Member><CoverageMap /></Member>} />
        <Route path="/coverage" element={<Member><CoverageMap /></Member>} />
        <Route path="/pricing" element={<Protected><Pricing /></Protected>} />
        <Route path="/legal" element={<Legal />} />
        <Route path="/legal/:slug" element={<Legal />} />
        <Route path="/admin" element={<Protected><Admin /></Protected>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <MemberOnlyAssistant />
      <Footer />
      <ConsentBanner />
    </Layout>
  );
}

// The global AI assistant is a paid feature — only mount it for active members.
function MemberOnlyAssistant() {
  const { ent } = useBilling();
  return ent?.premium ? <AIAssistant /> : null;
}

export default function App() {
  return (
    <ThemeProvider attribute="class" defaultTheme="dark" forcedTheme="dark" enableSystem={false}>
      <BrowserRouter>
        <AuthProvider>
          <BillingProvider>
            <AnalystProvider>
              <AppRoutes />
              <Toaster position="top-center" />
            </AnalystProvider>
          </BillingProvider>
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}
